import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Box,
  Divider,
  Flex,
  HStack,
  Link,
  Skeleton,
  Spacer,
  VStack,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import {
  InvoiceComponentGQLType,
  InvoiceCreatedByTypeGQLType,
  InvoiceFragmentGQLType,
  InvoiceItemGQLType,
  TAccountGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { DownloadInvoicePreviewButton } from './create/DownloadInvoicePreviewButton';
import {
  getBankAccountDisplay,
  getInterestInvoiceItemLabel,
  getLoanLabel,
  invoiceHasCalculationMismatches,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import {
  Typography,
  TypographyVariant,
} from 'packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  getTAccountTypeLabel,
} from 'packages/common-frontend/utils';
import { useGetLoanRecordByIdQuery } from 'packages/loans/requests/loans.gql.generated';
import { useGetTAccountsQuery } from 'packages/statements/requests/ledger.gql.generated';

type PreviewInvoiceItem = Omit<
  InvoiceItemGQLType,
  'id' | 'invoiceId' | 'created_at' | 'updated_at' | 'accountingAccount'
>;

type PreviewInvoiceComponent = Omit<
  InvoiceComponentGQLType,
  'id' | 'invoiceId' | 'created_at' | 'updated_at'
>;

type PreviewInvoice = Omit<
  InvoiceFragmentGQLType,
  | '__typename'
  | 'created_at'
  | 'updated_at'
  | 'id'
  | 'invoiceItems'
  | 'paymentDestinationOrganization'
  | 'invoiceComponentsWithInvoiceItems'
  | 'createdByType'
> & {
  invoiceComponentsWithInvoiceItems: {
    invoiceComponent: PreviewInvoiceComponent;
    invoiceItems: PreviewInvoiceItem[];
  }[];
  __typename: 'PreviewInvoice';
};

const MismatchWarningAlert = ({
  onClick,
  onClickLabel,
  invoiceCreatedByType,
}: {
  onClick: () => void;
  onClickLabel: string;
  invoiceCreatedByType: InvoiceCreatedByTypeGQLType;
}) => {
  return (
    <Alert status="warning" size="md" p="1rem">
      <AlertIcon />
      <Flex direction="column" flexGrow={1}>
        <AlertTitle>
          Calculated amounts between
          {invoiceCreatedByType === 'ibs' ? ' IBS ' : ' input '} and Finley
          differ on this invoice.
        </AlertTitle>
      </Flex>
      <Button
        alignSelf="flex-start"
        variant="link"
        colorScheme="blue"
        aria-label="View"
        onClick={onClick}
      >
        {onClickLabel}
      </Button>
    </Alert>
  );
};

const CapitalizedInvoiceItemLabel = ({
  sourceLoanId,
  capitalizedLoanId,
}: {
  sourceLoanId: string;
  capitalizedLoanId: string;
}) => {
  const {
    data: loanRecordData,
    loading: isLoanRecordLoading,
    error: loanRecordError,
  } = useGetLoanRecordByIdQuery({
    variables: {
      loanId: capitalizedLoanId,
    },
  });

  if (loanRecordError) {
    return null;
  }

  if (isLoanRecordLoading || !loanRecordData?.loan) {
    return <Skeleton height="1rem" />;
  }

  const { loan } = loanRecordData;

  const capitalizedLoanLabel = getLoanLabel({
    loanId: loan.id,
    loanName: loan.name,
    externalLoanId: loan.externalLoanId ?? null,
    alwaysIncludeLoanName: true,
  });

  const capitalizationLabel =
    sourceLoanId === capitalizedLoanId
      ? 'Interest Capitalization to This Loan'
      : `Interest Capitalization to ${capitalizedLoanLabel}`;

  return <Typography variant="base-muted">{capitalizationLabel}</Typography>;
};

export const InvoiceDetailInformationRow = ({
  label,
  labelFontWeight = 'semibold',
  value,
  valueTypographyVariant = 'base-muted',
  valueTextAlign = 'right',
  valueOnClick,
  isSubRow = false,
}: {
  label: string;
  labelFontWeight?: 'normal' | 'semibold';
  value?: string;
  valueTextAlign?: 'left' | 'right';
  valueTypographyVariant?: TypographyVariant;
  valueOnClick?: () => void;
  isSubRow?: boolean; // used to indent the row if it's a sub-level row
}) => {
  return (
    <Flex
      alignItems="top"
      justifyContent="space-between"
      ml={isSubRow ? '24px' : undefined}
      my="8px"
    >
      <Typography fontWeight={labelFontWeight}>{label}</Typography>
      <Box
        onClick={valueOnClick}
        cursor={valueOnClick ? 'pointer' : 'default'}
        _hover={valueOnClick ? { textDecoration: 'underline' } : undefined}
        textAlign={valueTextAlign}
      >
        <Typography variant={valueTypographyVariant}>{value ?? '-'}</Typography>
      </Box>
    </Flex>
  );
};

const InvoiceItems = ({
  invoice,
  invoiceComponentIndex,
  tAccounts,
}: {
  invoice: InvoiceFragmentGQLType | PreviewInvoice;
  invoiceComponentIndex: number;
  tAccounts: TAccountGQLType[];
}) => {
  const { invoiceComponentsWithInvoiceItems } = invoice;
  const { invoiceItems, invoiceComponent } = invoiceComponentsWithInvoiceItems[
    invoiceComponentIndex
  ];

  const groupedInvoiceItems = useMemo(() => {
    const accruedInterestInvoiceItems: (
      | PreviewInvoiceItem
      | InvoiceItemGQLType
    )[] = [];
    const otherInvoiceItems: (PreviewInvoiceItem | InvoiceItemGQLType)[] = [];

    invoiceItems.forEach((invoiceItem) => {
      const tAccountDetails = tAccounts.find(
        (tAccount) => tAccount.id === invoiceItem.accountingAccountId,
      );
      if (tAccountDetails?.tAccount === 'accruedInterest') {
        accruedInterestInvoiceItems.push(invoiceItem);
      } else {
        otherInvoiceItems.push(invoiceItem);
      }
    });

    return { accruedInterestInvoiceItems, otherInvoiceItems };
  }, [invoiceItems, tAccounts]);

  if (groupedInvoiceItems === null || !tAccounts) {
    return <Skeleton width="full" h="100px" />;
  }

  return (
    <VStack width="full" gap="1rem">
      {groupedInvoiceItems.otherInvoiceItems.map((invoiceItem, idx) => {
        const tAccountDetails = tAccounts.find(
          (tAccount) => tAccount.id === invoiceItem.accountingAccountId,
        );
        if (!tAccountDetails) {
          return <></>;
        }

        const key = `other-${idx}-${invoiceItem.accountingAccountId}`;
        return (
          <HStack key={key} justifyContent="space-between" w="100%">
            <Typography variant="base-muted">
              {getTAccountTypeLabel(tAccountDetails.tAccount)}
            </Typography>
            <Typography>
              {dollarNumberWithCommas(invoiceItem.amount)}
            </Typography>
          </HStack>
        );
      })}

      {groupedInvoiceItems.accruedInterestInvoiceItems.length > 0 && (
        <>
          <HStack w="100%">
            <Typography variant="base-muted">Interest</Typography>
            <Spacer />
          </HStack>
          {groupedInvoiceItems.accruedInterestInvoiceItems.map(
            (accruedInterestInvoiceItem, idx) => {
              const key = `interest-${idx}-${accruedInterestInvoiceItem.accountingAccountId}`;

              return (
                <HStack key={key} w="100%">
                  <Box width="2em" />
                  {invoiceComponent.finleyLoanId &&
                  accruedInterestInvoiceItem.capitalizedLoanId ? (
                    <CapitalizedInvoiceItemLabel
                      sourceLoanId={invoiceComponent.finleyLoanId}
                      capitalizedLoanId={
                        accruedInterestInvoiceItem.capitalizedLoanId
                      }
                    />
                  ) : (
                    <Typography variant="base-muted">
                      {getInterestInvoiceItemLabel({
                        invoiceItem: accruedInterestInvoiceItem,
                        invoiceLoanId: invoiceComponent.finleyLoanId,
                      })}
                    </Typography>
                  )}
                  <Spacer />
                  <Typography>
                    {dollarNumberWithCommas(accruedInterestInvoiceItem.amount)}
                  </Typography>
                </HStack>
              );
            },
          )}
        </>
      )}
      <Divider />
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="semibold">Sub-Total</Typography>
        <Typography>
          {dollarNumberWithCommas(invoiceComponent.totalAmount)}
        </Typography>
      </HStack>
    </VStack>
  );
};

const LoanInvoiceComponentDetails = ({
  invoiceComponent,
  isPrincipalOnlyInvoiceComponent,
}: {
  invoiceComponent: InvoiceComponentGQLType | PreviewInvoiceComponent;
  isPrincipalOnlyInvoiceComponent: boolean;
}) => {
  const {
    data: loanData,
    error: loanError,
    loading: isLoanLoading,
  } = useGetLoanRecordByIdQuery({
    variables: {
      loanId: invoiceComponent.finleyLoanId,
    },
  });

  if (loanError) {
    throw loanError;
  }

  if (isLoanLoading || !loanData) {
    return <Skeleton width="full" h="400px" />;
  }

  const { loan: invoiceComponentLoan } = loanData;

  return (
    <HStack justifyContent="space-between" w="100%">
      <Flex>
        <Link
          as={RouterLink}
          to={`/loans/${invoiceComponentLoan?.id}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Typography
            colorScheme="blue"
            fontWeight="medium"
            variant="base-muted"
          >
            {invoiceComponentLoan?.name || '-'}&nbsp;
          </Typography>
        </Link>
        {invoiceComponentLoan?.externalLoanId && (
          <Typography variant="base-muted">
            {`(${invoiceComponentLoan.externalLoanId})`}
          </Typography>
        )}
      </Flex>
      {!isPrincipalOnlyInvoiceComponent && (
        <Typography fontWeight="medium">
          {commonDate.fullDateStrToHumanDateString(
            invoiceComponent.accrualStartDate,
          )}{' '}
          -{' '}
          {commonDate.fullDateStrToHumanDateString(
            invoiceComponent.accrualEndDate,
          )}
        </Typography>
      )}
    </HStack>
  );
};

const FacilityInvoiceComponentDetails = ({
  invoiceComponent,
  creditFacilityName,
}: {
  invoiceComponent: InvoiceComponentGQLType | PreviewInvoiceComponent;
  creditFacilityName: string;
}) => {
  return (
    <HStack justifyContent="space-between" w="100%">
      <Typography fontWeight="medium" variant="base-muted">
        {creditFacilityName} Facility Fees
      </Typography>

      <Typography fontWeight="medium">
        {commonDate.fullDateStrToHumanDateString(
          invoiceComponent.accrualStartDate,
        )}{' '}
        -{' '}
        {commonDate.fullDateStrToHumanDateString(
          invoiceComponent.accrualEndDate,
        )}
      </Typography>
    </HStack>
  );
};

export const InvoiceDetailInformation = ({
  invoice,
  isPreview = false,
  mismatchAlertOnClick,
  mismatchAlertOnClickLabel,
}: {
  invoice: InvoiceFragmentGQLType | PreviewInvoice;
  isPreview?: boolean;
  mismatchAlertOnClick: () => void;
  mismatchAlertOnClickLabel: string;
}) => {
  const {
    creditFacilityId,
    creditFacility,
    destinationBankAccount,
    paymentSourceOrganization,
    invoiceComponentsWithInvoiceItems,
  } = invoice;

  const { data: tAccountsData, error: tAccountsError } = useGetTAccountsQuery({
    variables: {
      creditFacilityId,
    },
  });

  const hasMismatches = useMemo(() => {
    return invoiceHasCalculationMismatches(
      invoice.invoiceCalculationComparisons,
    );
  }, [invoice]);

  if (!tAccountsData) {
    return <Skeleton width="full" h="400px" />;
  }

  if (tAccountsError) {
    throw tAccountsError;
  }

  return (
    <Box width="full">
      {isPreview && (
        <Flex justifyContent="flex-end" mb="1rem">
          <DownloadInvoicePreviewButton creditFacilityId={creditFacility.id} />
        </Flex>
      )}

      <VStack align="flex-start" gap="3rem">
        {hasMismatches && invoice.__typename === 'Invoice' && (
          <MismatchWarningAlert
            onClick={mismatchAlertOnClick}
            onClickLabel={mismatchAlertOnClickLabel}
            invoiceCreatedByType={invoice.createdByType}
          />
        )}
        {/* Payment Details */}
        <VStack w="100%" gap="1rem" align="flex-start">
          <Typography variant="base" fontWeight="bold" trim>
            Information
          </Typography>
          <Divider />
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">Due Date</Typography>
            <Typography fontWeight="semibold">
              {commonDate.fullDateStrToHumanDateString(invoice.dueDate)}
            </Typography>
          </HStack>
          {invoice.status === 'paid' && invoice.paymentDate && (
            <HStack justifyContent="space-between" w="100%">
              <Typography variant="base-muted">Payment Date</Typography>
              <Typography fontWeight="semibold">
                {commonDate.fullDateStrToHumanDateString(invoice.paymentDate)}
              </Typography>
            </HStack>
          )}
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">Facility Name</Typography>
            <Link
              as={RouterLink}
              to={`/portfolio/summary?creditFacility=${creditFacility.id}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Typography
                colorScheme="blue"
                fontWeight="semibold"
                variant="base-muted"
              >
                {_.startCase(creditFacility.name)}
              </Typography>
            </Link>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">External Facility ID</Typography>
            <Typography fontWeight="semibold">
              {creditFacility.externalFacilityId ?? '-'}
            </Typography>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">Borrower</Typography>
            <Typography fontWeight="semibold">
              {_.startCase(paymentSourceOrganization.name)}
            </Typography>
          </HStack>
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">Memo</Typography>
            <Typography fontWeight="semibold">{invoice.memo ?? '-'}</Typography>
          </HStack>
        </VStack>

        {/* Payment Instructions */}
        <VStack w="100%" gap="1rem" align="flex-start">
          <Typography variant="base" fontWeight="bold" trim>
            Payment Instructions
          </Typography>
          <Divider />

          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base-muted">
              Destination Bank Account
            </Typography>
            <Typography fontWeight="semibold">
              {destinationBankAccount
                ? getBankAccountDisplay({
                    institutionName: destinationBankAccount.institutionName,
                    accountName: destinationBankAccount.name,
                    accountNumber: destinationBankAccount.accountNumber,
                  })
                : '-'}
            </Typography>
          </HStack>
        </VStack>

        {/* Invoice Line Items */}
        <VStack w="100%" gap="1rem" align="flex-start">
          <Typography variant="base" fontWeight="bold" trim>
            Invoice
          </Typography>
          <Divider />
        </VStack>
        <VStack w="100%" gap="3rem" align="flex-start">
          {invoiceComponentsWithInvoiceItems.map(
            ({ invoiceComponent, invoiceItems }, invoiceComponentIndex) => {
              const key = invoiceComponentIndex;

              const tAccountDetailsForFirstInvoiceItem = tAccountsData.getTAccounts.find(
                (tAccount) =>
                  tAccount.id === invoiceItems[0].accountingAccountId,
              );

              const isPrincipalOnlyInvoiceComponent =
                invoiceComponent.invoiceComponentType === 'loan' &&
                invoiceItems.length === 1 &&
                tAccountDetailsForFirstInvoiceItem?.tAccount === 'longTermDebt';

              return (
                <VStack
                  key={key}
                  w="100%"
                  borderRadius="8px"
                  border="1px"
                  borderColor={colors.gray[200]}
                  p="2rem"
                  gap="1rem"
                >
                  {invoiceComponent.invoiceComponentType === 'loan' ? (
                    <LoanInvoiceComponentDetails
                      invoiceComponent={invoiceComponent}
                      isPrincipalOnlyInvoiceComponent={
                        isPrincipalOnlyInvoiceComponent
                      }
                    />
                  ) : (
                    <FacilityInvoiceComponentDetails
                      invoiceComponent={invoiceComponent}
                      creditFacilityName={creditFacility.name}
                    />
                  )}
                  <InvoiceItems
                    invoice={invoice}
                    invoiceComponentIndex={invoiceComponentIndex}
                    tAccounts={tAccountsData.getTAccounts}
                  />
                </VStack>
              );
            },
          )}
        </VStack>
        <Box
          border="1px"
          borderColor={colors.gray[200]}
          borderRadius="8px"
          p="1rem"
          backgroundColor={colors.gray[50]}
          w="100%"
        >
          <HStack justifyContent="space-between" w="100%">
            <Typography variant="base" fontWeight="bold" trim>
              Invoice Total
            </Typography>
            <Typography fontWeight="semibold">
              {dollarNumberWithCommas(invoice.totalAmount)}
            </Typography>
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};
